import {FormControl, FormField} from 'platform/components';
import {Grid, GridItem} from 'platform/foundation';
import {useCurrencySymbolFormatter} from 'platform/locale';

import {useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {indexBy, isNil} from 'ramda';
import {isString} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {useTenant, useVatRatesOptions} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useDebouncedCallback} from 'shared/web';

import {roundToTwoDecimals} from '../../RentalOrderHeader/utils/roundToTwoDecimals';
import {ReturnVehicleFormValues} from '../types/ReturnVehicleFormValues';

interface ChargedFuelPriceRowProps extends RequiredTestIdProps {
  control: FormControl<ReturnVehicleFormValues>;
  formApi: UseFormReturn<ReturnVehicleFormValues>;
}
export function ChargedFuelPriceRow(props: ChargedFuelPriceRowProps) {
  const [vatType, isCustomPriceEnabled] = useWatch({
    control: props.control,
    name: [`rentPrice.chargedFuel.price.vatType`, 'isCustomPriceEnabled'],
  });

  const [lastTouchedField, setLastTouchedField] = useState<'withVat' | 'withoutVat'>('withoutVat');

  const [vatRatesOptions] = useVatRatesOptions();
  const vatRatesByCode = indexBy((vatRate) => vatRate.value, vatRatesOptions);
  const formatCurrencySymbol = useCurrencySymbolFormatter();
  const {tenantCurrency} = useTenant();

  const handleRecalculatePriceWithVat = useDebouncedCallback(
    (priceWithoutVat: number | null, changedVatRate?: string) => {
      setLastTouchedField('withoutVat');
      const absoluteVatRate = vatRatesByCode?.[changedVatRate ?? vatType ?? '']?.rate;

      if (isNil(priceWithoutVat) || isNil(absoluteVatRate)) {
        return;
      }

      const rate = parseFloat(absoluteVatRate);
      const newPriceWithVat = roundToTwoDecimals(priceWithoutVat * (1 + rate / 100));
      const newVatPrice = roundToTwoDecimals(newPriceWithVat - priceWithoutVat);
      props.formApi.setValue(`rentPrice.chargedFuel.price.withVat`, newPriceWithVat);
      props.formApi.setValue(`rentPrice.chargedFuel.price.vat`, newVatPrice);
    },
    200
  );

  const handleRecalculatePriceWithoutVat = useDebouncedCallback(
    (priceWithVat: number | null, changedVatRate?: string) => {
      setLastTouchedField('withVat');
      const absoluteVatRate = vatRatesByCode?.[changedVatRate ?? vatType ?? '']?.rate;

      if (isNil(priceWithVat) || isNil(absoluteVatRate)) {
        return;
      }

      const rate = parseFloat(absoluteVatRate);
      const newPriceWithoutVat = roundToTwoDecimals(priceWithVat / (1 + rate / 100));
      const newVatPrice = roundToTwoDecimals(priceWithVat - newPriceWithoutVat);
      props.formApi.setValue(`rentPrice.chargedFuel.price.withoutVat`, newPriceWithoutVat);
      props.formApi.setValue(`rentPrice.chargedFuel.price.vat`, newVatPrice);
    },
    200
  );

  const handleRecalculateAfterVatRateChange = (vatRate: string | number | null) => {
    if (!isString(vatRate)) {
      return;
    }

    const values = props.formApi.getValues('rentPrice.chargedFuel.price');

    if (lastTouchedField === 'withoutVat' && values?.withoutVat) {
      handleRecalculatePriceWithVat(values.withoutVat, vatRate);
    }

    if (lastTouchedField === 'withVat' && values?.withVat) {
      handleRecalculatePriceWithoutVat(values.withVat, vatRate);
    }
  };

  return (
    <Grid columns={4}>
      <Grid columns={4} spacing={2}>
        <FormField
          control={props.control}
          type="number"
          name="rentPrice.chargedFuel.amount"
          label={i18n.t('page.rentalOrder.labels.chargedFuel')}
          suffix={i18n.t('general.metric.l')}
          isDisabled={isCustomPriceEnabled}
          data-testid={suffixTestId('amount', props)}
        />

        <GridItem span={3}>
          <FormField
            control={props.control}
            type="currency"
            name="rentPrice.chargedFuel.price.withoutVat"
            label={i18n.t('page.rentalOrder.labels.chargedFuelWithoutVat')}
            currency={tenantCurrency}
            decimalPlaces={2}
            isDisabled={isCustomPriceEnabled}
            onChange={(value) => handleRecalculatePriceWithVat(value)}
            data-testid={suffixTestId(`priceWithoutVat`, props)}
          />
        </GridItem>
      </Grid>

      <FormField
        control={props.control}
        type="currency"
        name="rentPrice.chargedFuel.price.withVat"
        label={i18n.t('page.rentalOrder.labels.chargedFuelWithVat')}
        currency={tenantCurrency}
        decimalPlaces={2}
        isDisabled={isCustomPriceEnabled}
        onChange={(value) => handleRecalculatePriceWithoutVat(value)}
        data-testid={suffixTestId(`priceWithVat`, props)}
      />

      <FormField
        control={props.control}
        type="currency"
        name="rentPrice.chargedFuel.price.vat"
        decimalPlaces={2}
        label={`${i18n.t('entity.vehicle.labels.vat')} ${formatCurrencySymbol(tenantCurrency)}`}
        currency={tenantCurrency}
        isDisabled
        data-testid={suffixTestId(`priceVat`, props)}
      />

      <FormField
        control={props.control}
        type="choice"
        name="rentPrice.chargedFuel.price.vatType"
        label={i18n.t('entity.vehicle.labels.vat')}
        options={vatRatesOptions}
        isNotClearable
        isDisabled={isCustomPriceEnabled}
        onChange={(value) => handleRecalculateAfterVatRateChange(value)}
        data-testid={suffixTestId(`priceVatRate`, props)}
      />
    </Grid>
  );
}
